/**
 * Client-side PDF → JPEG rasteriser.
 *
 * Why? On production we hit Cloudflare 524s when posting multi-page
 * EICR PDFs (5-10 pages, 3-5 MB) to `/api/certificates/scan-previous`.
 * The backend rasterise + OCR is already async and returns in ~400 ms
 * locally, but something between Cloudflare and the Emergent origin
 * stalls on large request bodies — 2-page PDFs work, 5-10 page PDFs
 * time out. Pre-splitting the PDF into individual JPEG pages on the
 * client side eliminates this entirely:
 *
 *   • Each upload is a single multipart with N small JPEG parts
 *     (each ~150-400 KB at scale=1.5) instead of one big PDF blob.
 *   • Backend never needs to import pypdfium2 / call _rasterise_pdf
 *     — `_is_pdf(...) === false` branch just appends bytes directly.
 *   • If pdfjs fails on a weird PDF, we fall back to posting the raw
 *     file so the server-side rasteriser still gets a chance.
 *
 * Returns: `File[]` (one JPEG per page, named `<basename>-p1.jpg` …)
 */
import { pdfjs } from 'react-pdf';

// Same-origin worker — the deploy ships `/pdf.worker.min.mjs` in
// `frontend/public/`. Setting it here too (in addition to
// `PdfPreviewDialog.jsx`) guarantees the cert-scan flow has a worker
// configured even when the preview dialog never gets loaded.
if (!pdfjs.GlobalWorkerOptions.workerSrc) {
  pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';
}

const DEFAULT_SCALE = 1.5;   // 1.5x = ~108 dpi on A4 — legible for OCR, ~250 KB JPEG
const JPEG_QUALITY = 0.85;
const MAX_PAGES = 50;        // sanity cap so we never hammer the model

/**
 * Convert a single PDF File to an array of JPEG Files (one per page).
 * Throws on unreadable PDFs so the caller can decide whether to fall
 * back to a raw PDF upload.
 */
export async function rasterisePdfFileToJpegs(pdfFile, { scale = DEFAULT_SCALE } = {}) {
  if (!pdfFile || pdfFile.type !== 'application/pdf') {
    throw new Error('rasterisePdfFileToJpegs: input must be a PDF File');
  }
  const buffer = await pdfFile.arrayBuffer();
  const loadingTask = pdfjs.getDocument({ data: buffer });
  const pdf = await loadingTask.promise;
  const pageCount = Math.min(pdf.numPages, MAX_PAGES);
  const base = (pdfFile.name || 'cert').replace(/\.pdf$/i, '');
  const out = [];
  for (let i = 1; i <= pageCount; i++) {
    const page = await pdf.getPage(i);
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    const ctx = canvas.getContext('2d');
    // White background — some PDFs have transparent backgrounds that
    // render black when flattened to JPEG, killing OCR contrast.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: ctx, viewport }).promise;
    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('canvas.toBlob returned null'))),
        'image/jpeg',
        JPEG_QUALITY,
      );
    });
    out.push(new File([blob], `${base}-p${i}.jpg`, { type: 'image/jpeg' }));
  }
  return out;
}

/**
 * Best-effort entry point used by the certificate-scan flow. For any
 * `application/pdf` input, attempts the client-side rasterise. On
 * any failure (corrupt PDF, password-protected, pdfjs quirks) falls
 * back to returning the original File so the server still gets a
 * chance with its own pdfium-based rasteriser.
 */
export async function preprocessFilesForUpload(files) {
  const out = [];
  for (const f of files) {
    if (f.type === 'application/pdf') {
      try {
        const jpegs = await rasterisePdfFileToJpegs(f);
        out.push(...jpegs);
        continue;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('Client-side PDF rasterise failed — falling back to raw PDF upload', e);
      }
    }
    out.push(f);
  }
  return out;
}
